/**
 * The app shell. Home is the front door; from it: Today (the scan screen, running today's planned
 * route), Plan (the week planner), Load (scanning pots into the car) and Reports. It owns the two
 * stores (the placement record and the planner's intent) and derives everything once for all views.
 */
import { useMemo, useState, useSyncExternalStore } from "react";
import {
  assetRegister,
  buildHistory,
  catalogFrom,
  createAssessor,
  headroom,
  monthSummary,
  planWeek,
  taxYearOf,
  type CalendarDate,
} from "@hitl/rotations-domain";
import type { DemoTenant } from "./demo/demoTenant";
import type { Fixture } from "./demo/fixtures";
import type { EventLog } from "./state/eventLog";
import type { PlannerStore } from "./state/plannerStore";
import { sparesOn, upcomingRouteDays } from "./state/plannerState";
import { deriveDay, type DaySetup } from "./state/session";
import { ScanScreen } from "./scan/ScanScreen";
import { PlanScreen } from "./plan/PlanScreen";
import { LoadView } from "./plan/LoadView";
import { NoRouteToday } from "./plan/NoRouteToday";
import { Home, type HomeStatus } from "./home/Home";
import { Reports } from "./reports/Reports";
import { SettingsScreen } from "./settings/Settings";
import type { PersistedStore } from "./state/persistedStore";
import type { SettingsState } from "./state/settingsState";

export type View =
  | { readonly kind: "home" }
  | { readonly kind: "today" }
  | { readonly kind: "plan" }
  | { readonly kind: "reports" }
  | { readonly kind: "settings" }
  | { readonly kind: "load"; readonly on: CalendarDate; readonly from: "home" | "plan" };

/** Clients with this many eligible pots or fewer raise a Home alert. */
const LOW_HEADROOM = 3;

export function App(props: {
  demo: DemoTenant;
  log: EventLog;
  planner: PlannerStore;
  settings: PersistedStore<SettingsState>;
  today: CalendarDate;
  fixture?: Fixture;
  initialView?: View;
}) {
  const { demo, log, planner, today, fixture } = props;
  const settings = useSyncExternalStore(props.settings.subscribe, props.settings.get);
  const events = useSyncExternalStore(log.subscribe, log.all);
  const syncState = useSyncExternalStore(log.subscribe, log.syncState);
  const pending = useSyncExternalStore(log.subscribe, log.pending);
  const plannerState = useSyncExternalStore(planner.subscribe, planner.get);
  const [view, setView] = useState<View>(props.initialView ?? { kind: "home" });
  // The catalog and rules are hers to edit (Settings); everything downstream derives from them.
  const catalog = useMemo(() => catalogFrom(settings.catalog), [settings.catalog]);
  const policy = useMemo(() => ({ windowMonths: settings.rules.windowMonths, scope: "client" as const }), [settings.rules.windowMonths]);
  const historyKnownSince = settings.rules.historyKnownSince;
  const defaultSpares = settings.rules.defaultSpares;
  const home = () => setView({ kind: "home" });

  const history = useMemo(() => buildHistory(events), [events]);
  const assessor = useMemo(() => createAssessor({ catalog, history, policy, historyKnownSince }), [catalog, history, policy, historyKnownSince]);

  const routeDays = useMemo(() => upcomingRouteDays(plannerState, today), [plannerState, today]);
  const week = useMemo(
    () =>
      planWeek(
        routeDays.map((d) => ({ on: d.on, slotIds: d.route.slotIds })),
        { catalog, realEvents: events, policy, historyKnownSince },
        {
          spares: (on) => sparesOn(plannerState, on, defaultSpares),
          pinned: (on) => {
            const pins = plannerState.pins[on];
            return pins ? new Map(Object.entries(pins)) : undefined;
          },
        },
      ),
    [routeDays, catalog, events, policy, historyKnownSince, plannerState, defaultSpares],
  );

  const todayPlan = week.days.find((d) => d.on === today);
  const todayRoute = routeDays.find((d) => d.on === today)?.route;
  const setup: DaySetup | null = useMemo(
    () =>
      todayPlan && todayRoute
        ? { catalog, policy, historyKnownSince, route: { on: today, slotIds: todayRoute.slotIds }, plan: todayPlan.plan }
        : null,
    // Today's plan is judged from this morning, so it stays steady while she records swaps.
    [todayPlan, todayRoute, catalog, policy, historyKnownSince, today],
  );

  const homeStatus = useMemo((): HomeStatus => {
    const day = setup ? deriveDay(setup, events) : null;
    const todayDone = day ? day.nextOpenIndex === null : true;
    // Load the car: today's route while it's on, otherwise the next route day.
    const loadDay = (!todayDone && todayPlan) || week.days.find((d) => d.on > today);
    const nextRoute = routeDays.find((d) => d.on > today);
    const register = assetRegister(catalog, history, taxYearOf(today));
    const low = headroom(catalog, history, assessor, today).filter((h) => h.eligible <= LOW_HEADROOM);
    const writeOffs = monthSummary(history, today.slice(0, 7)).writeOffs.length;
    const alerts = [
      ...low.map((h) => `${catalog.client(h.clientId)?.name ?? h.clientId}: ${h.eligible} pots left`),
      ...(register.totals.missingCost ? [`${register.totals.missingCost} pots have no cost`] : []),
      ...(writeOffs ? [`${writeOffs} write-off${writeOffs === 1 ? "" : "s"} this month`] : []),
    ];
    return {
      today,
      hour: new Date().getHours(),
      syncState,
      pending,
      ...(day && todayRoute ? { route: { name: todayRoute.name, day } } : {}),
      ...(loadDay
        ? {
            load: {
              on: loadDay.on,
              total: loadDay.report.loadList.length,
              loaded: loadDay.report.loadList.filter((p) => (plannerState.loaded[loadDay.on] ?? []).includes(p)).length,
            },
          }
        : {}),
      ...(nextRoute ? { nextPlan: { on: nextRoute.on, routeName: nextRoute.route.name } } : {}),
      planProblems: week.problems.length,
      alerts,
    };
  }, [setup, events, todayPlan, week, today, routeDays, catalog, history, assessor, syncState, pending, todayRoute, plannerState.loaded]);

  if (view.kind === "home") {
    return (
      <Home
        status={homeStatus}
        onTrack={() => setView({ kind: "today" })}
        onLoad={() => homeStatus.load && setView({ kind: "load", on: homeStatus.load.on, from: "home" })}
        onPlan={() => setView({ kind: "plan" })}
        onReports={() => setView({ kind: "reports" })}
        onSettings={() => setView({ kind: "settings" })}
      />
    );
  }

  if (view.kind === "settings") {
    return (
      <SettingsScreen
        store={props.settings}
        history={history}
        log={log}
        planner={planner}
        onBack={home}
        onResetDemo={() => {
          log.resetLocal();
          planner.update(() => demo.planner);
          props.settings.update(() => demo.settings);
          home();
        }}
        {...(fixture?.settings ? { fixture: fixture.settings } : {})}
      />
    );
  }

  if (view.kind === "reports") {
    return (
      <Reports
        catalog={catalog}
        history={history}
        assessor={assessor}
        log={log}
        today={today}
        business={settings.business}
        onBack={home}
        {...(fixture?.reports ? { fixture: fixture.reports } : {})}
      />
    );
  }

  if (view.kind === "plan") {
    return (
      <PlanScreen
        catalog={catalog}
        today={today}
        week={week}
        planner={plannerState}
        store={planner}
        onBack={home}
        onLoad={(on) => setView({ kind: "load", on, from: "plan" })}
        defaultSpares={defaultSpares}
        {...(fixture?.plan ? { fixture: fixture.plan } : {})}
      />
    );
  }

  if (view.kind === "load") {
    const day = week.days.find((d) => d.on === view.on);
    const route = routeDays.find((d) => d.on === view.on)?.route;
    if (day && route) {
      return (
        <LoadView
          catalog={catalog}
          day={day}
          routeName={route.name}
          loaded={plannerState.loaded[view.on] ?? []}
          store={planner}
          backLabel={view.from === "home" ? "Home" : "Plan"}
          onBack={() => setView(view.from === "home" ? { kind: "home" } : { kind: "plan" })}
          {...(fixture?.load ? { fixture: fixture.load } : {})}
        />
      );
    }
  }

  if (!setup || !todayRoute) return <NoRouteToday today={today} onOpenPlan={() => setView({ kind: "plan" })} onHome={home} />;

  return (
    <ScanScreen
      key={`${today}:${todayRoute.id}`}
      setup={setup}
      log={log}
      today={today}
      routeName={todayRoute.name}
      onOpenPlan={() => setView({ kind: "plan" })}
      onHome={home}
      {...(fixture ? { fixture } : {})}
    />
  );
}
